import { FaArrowRight } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState, type JSX } from 'react';
import logoFull from '@/assets/svg/static/logo-full.svg';
import tick from '@/assets/svg/static/tick.svg';
import { SkeletonLoader } from '@/components/ui';
import ProcessingScreen from '@/components/ui/ProcessingScreen';
import { fetchKeyframesForVideoId } from '@/services/slaydAPI';
import { updatedImageUrl } from '@/utils/formatting';
import { openSimilarProductInApp } from '@/utils/openInApp';
import styles from './SelectFrame.module.css';

const SelectFrame = (): JSX.Element => {
  const [selectedImages, setSelectedImages] = useState<number[]>([]);
  const [frameImages, setFrameimages] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [isSnackbarHiding, setIsSnackbarHiding] = useState<boolean>(false);
  const [showProcessing, setShowProcessing] = useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const toggleSelection = (index: number): void => {
    if (selectedImages.includes(index)) {
      setSelectedImages(selectedImages.filter((i) => i !== index));
    } else {
      if (selectedImages.length < 3) {
        setSelectedImages([...selectedImages, index]);
      } else {
        // Show snackbar
        setShowAlert(true);

        // Start hiding animation after 2.7 seconds
        setTimeout(() => {
          setIsSnackbarHiding(true);
        }, 2700);

        // Actually hide the snackbar after animation completes (300ms)
        setTimeout(() => {
          setShowAlert(false);
          setIsSnackbarHiding(false);
        }, 3000);
      }
    }
  };

  const videoId = searchParams.get('id');
  const senderId = searchParams.get('sender_id');

  useEffect(() => {
    if (senderId) {
      sessionStorage.setItem('instagramSenderId', senderId);
    }
  }, [senderId]);

  useEffect(() => {
    const fetchFrames = async (): Promise<void> => {
      if (!videoId) {
        console.error('No video id provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const images = await fetchKeyframesForVideoId(videoId);
        setFrameimages(images);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFrames();
  }, [videoId]);

  const handleContinue = (): void => {
    const showOnboardingVideo = searchParams.get('showonboardingvideo');
    const selectedImageUrls = selectedImages.map((index) => frameImages[index]);

    if (showOnboardingVideo === 'true') {
      setShowProcessing(true);
    } else {
      // Try to open in app first, with fallback to existing behavior
      openSimilarProductInApp({
        imageUrls: selectedImageUrls,
        fallback: () => {
          // Fallback to existing behavior if app doesn't open
          handleProcessingComplete(selectedImageUrls);
        },
      });
    }
  };

  const handleProcessingComplete = (imageUrls: string[]): void => {
    setShowProcessing(false);

    // Try to open in app first, with fallback to existing behavior
    openSimilarProductInApp({
      imageUrls,
      fallback: () => {
        // Fallback to existing behavior if app doesn't open
        navigate('/similar-product', {
          state: {
            imageUrls,
            videoId,
            senderId,
          },
        });
      },
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.logoContainer}>
        <img
          src={logoFull}
          alt="Slayd Logo"
          className={styles.logo}
          onClick={() => navigate('/')}
        />
      </div>
      <div className={styles.scrollContainer}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Select the best frames</h1>
          <h1 className={styles.subTitle}>
            Choose frames where clothing is fully visible
          </h1>
        </div>
        {loading ? (
          <div className={styles.loadingContainer}>
            {SkeletonLoader.tripleColumnList(4)}
          </div>
        ) : (
          <>
            <div className={styles.grid}>
              {frameImages.map((image, index) => (
                <div
                  className={styles.imageContainer}
                  key={index}
                  onClick={() => toggleSelection(index)}
                >
                  <img
                    src={updatedImageUrl(image)}
                    alt={`Frame ${index}`}
                    className={styles.image}
                  />

                  {selectedImages.includes(index) && (
                    <div className={styles.tickOverlay}>
                      <img
                        src={tick}
                        alt="Selected"
                        className={styles.tickIcon}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Snackbar notification */}
        {showAlert && (
          <div
            className={`${styles.snackbar} ${isSnackbarHiding ? styles.hiding : ''}`}
          >
            <p>You can select upto 3 frames only</p>
          </div>
        )}
      </div>

      {/* Continue button that appears when images are selected */}

      <div className={styles.buttonContainer}>
        <button
          className={`${styles.continueButton} ${
            selectedImages.length === 0 ? styles.disabledButton : ''
          }`}
          disabled={selectedImages.length === 0}
          onClick={handleContinue}
        >
          Continue
          <FaArrowRight />
        </button>
      </div>

      {/* Processing Screen Overlay */}
      {showProcessing && (
        <ProcessingScreen
          imageUrls={selectedImages.map((index) => frameImages[index])}
          onComplete={(imageUrls) => handleProcessingComplete(imageUrls)}
        />
      )}
    </div>
  );
};

export default SelectFrame;
